import { Component, Input, OnInit, OnDestroy, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-image-tag',
  imports: [CommonModule],
  templateUrl: './image-tag.component.html',
  styleUrl: './image-tag.component.css'
})
export class ImageTagComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() src: string = '';
  @Input() alt: string = '';
  @Input() class: string = '';
  @Input() width?: number;
  @Input() height?: number;
  @Input() preventDownload: boolean = true;
  @Input() placeholder: string = '';

  @ViewChild('canvasRef', { static: false }) canvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('containerRef', { static: false }) containerRef!: ElementRef<HTMLDivElement>;

  private image: HTMLImageElement | null = null;
  private canvasContext: CanvasRenderingContext2D | null = null;
  imageLoaded = false;

  ngOnInit(): void {
    // Prevent context menu (right-click)
    if (this.preventDownload) {
      window.addEventListener('contextmenu', this.preventDefaultContextMenu);
      window.addEventListener('dragstart', this.preventDefaultDrag);
    }
  }

  ngAfterViewInit(): void {
    if (this.src) {
      this.loadImage();
    }
  }

  ngOnDestroy(): void {
    // Clean up event listeners
    if (this.preventDownload) {
      window.removeEventListener('contextmenu', this.preventDefaultContextMenu);
      window.removeEventListener('dragstart', this.preventDefaultDrag);
    }
  }

  private loadImage(): void {
    if (!this.canvasRef) {
      setTimeout(() => this.loadImage(), 100);
      return;
    }

    const canvas = this.canvasRef.nativeElement;
    this.canvasContext = canvas.getContext('2d', { willReadFrequently: true });

    if (!this.canvasContext) {
      console.error('Failed to get canvas context');
      return;
    }

    this.image = new Image();
    this.image.crossOrigin = 'anonymous';

    this.image.onload = () => {
      this.drawImageToCanvas();
      this.imageLoaded = true;
    };

    this.image.onerror = () => {
      console.error('Failed to load image:', this.src);
      if (this.placeholder) {
        this.src = this.placeholder;
        this.loadImage();
      }
    };

    this.image.src = this.src;
  }

  private drawImageToCanvas(): void {
    if (!this.canvasContext || !this.image || !this.canvasRef) {
      return;
    }

    const canvas = this.canvasRef.nativeElement;
    const container = this.containerRef?.nativeElement;

    // Set canvas dimensions
    const displayWidth = this.width || container?.clientWidth || this.image.width;
    const displayHeight = this.height || container?.clientHeight || this.image.height;

    canvas.width = displayWidth;
    canvas.height = displayHeight;

    // Clear canvas
    this.canvasContext.clearRect(0, 0, canvas.width, canvas.height);

    // Draw image to canvas
    this.canvasContext.drawImage(this.image, 0, 0, canvas.width, canvas.height);

    // Apply watermark or protection overlay (optional)
    if (this.preventDownload) {
      this.applyProtectionOverlay();
    }
  }

  private applyProtectionOverlay(): void {
    if (!this.canvasContext || !this.canvasRef) {
      return;
    }

    const canvas = this.canvasRef.nativeElement;

    // Add invisible watermark or overlay
    this.canvasContext.fillStyle = 'rgba(0, 0, 0, 0)';
    this.canvasContext.fillRect(0, 0, canvas.width, canvas.height);

    // Disable right-click on canvas
    canvas.oncontextmenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Prevent drag
    canvas.ondragstart = (e) => {
      e.preventDefault();
      return false;
    };
  }

  private preventDefaultContextMenu = (e: Event): void => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'CANVAS' || target.closest('app-image-tag')) {
      e.preventDefault();
    }
  };

  private preventDefaultDrag = (e: Event): void => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'CANVAS' || target.closest('app-image-tag')) {
      e.preventDefault();
    }
  };

  // Method to update image source dynamically
  updateImage(newSrc: string): void {
    this.src = newSrc;
    this.imageLoaded = false;
    this.loadImage();
  }

  // Get canvas data URL (for debugging or advanced use cases)
  getCanvasDataUrl(): string | null {
    if (!this.canvasRef || !this.imageLoaded) {
      return null;
    }
    return this.canvasRef.nativeElement.toDataURL();
  }
}
